import { ArrowLeftIcon } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { ButtonHTMLAttributes, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '@components';
import { ROUTES } from '@constants';
import { Translation, VariantType } from '@enums';
import { useAppDispatch, useFormSubmissionStatus } from '@hooks';
import { clearLoginError } from '@store';
import type { FormMetadata, LabelPayloadProps } from '@types';
import { cn } from '@utils';

interface LoginButtonGroupParams extends ButtonHTMLAttributes<HTMLButtonElement> {
  label?: LabelPayloadProps;
  formParams: FormMetadata;
  hasNavigate?: boolean;
  isValid?: boolean;
}
export const LoginButtonGroup: FC<LoginButtonGroupParams> = ({
  label,
  formParams,
  hasNavigate = false,
  disabled = false,
  className = '',
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t: tButton } = useTranslation(Translation.SignIn, { keyPrefix: 'button' });
  const { t: tCommon } = useTranslation(Translation.Common, { keyPrefix: 'button' });

  const isFormSubmitting = useFormSubmissionStatus(formParams);

  const handleNavigateBackToLogin = () => {
    dispatch(clearLoginError());
    navigate(ROUTES.login);
  };

  return (
    <motion.div
      className="flex justify-end pt-5 flex-col gap-7"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.3 }}
    >
      <Button
        loading={isFormSubmitting}
        disabled={disabled}
        type="submit"
        className={cn(
          'rounded-lg text-theme-text-main bg-sky-200 bg-opacity-70',
          'disabled:bg-theme-primary-dark disabled:text-white disabled:opacity-55',
          className,
        )}
      >
        {label?.confirmButton ?? tCommon('confirm')}
      </Button>
      {hasNavigate && (
        <Button
          disabled={isFormSubmitting}
          variant={VariantType.Transparent}
          onClick={handleNavigateBackToLogin}
          className="px-2.5 text-gray-400/85 rounded-lg disabled:bg-transparent enabled:shadow-none"
          adornment={<ArrowLeftIcon weight="bold" className="size-4" />}
        >
          {label?.customButton ?? tButton('backToLogin')}
        </Button>
      )}
    </motion.div>
  );
};
